import "$styles/index.css"
import "$styles/syntax-highlighting.css"

import "@awesome.me/webawesome/dist/styles/themes/default.css"
import "@awesome.me/webawesome/dist/components/icon/icon.js"
import "@awesome.me/webawesome/dist/components/card/card.js"
import "@awesome.me/webawesome/dist/components/badge/badge.js"
import "@awesome.me/webawesome/dist/components/tab-group/tab-group.js"
import "@awesome.me/webawesome/dist/components/tab/tab.js"
import "@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js"
import "@awesome.me/webawesome/dist/components/spinner/spinner.js"
import "@awesome.me/webawesome/dist/components/details/details.js"
import "@awesome.me/webawesome/dist/components/tooltip/tooltip.js"

import components from "$components/**/*.{js,jsx,js.rb,css}"

const THEME_STORAGE_KEY = "slp-calculator-theme"
const THEME_LABELS = {
  auto: "Auto theme",
  light: "Light theme",
  dark: "Dark theme",
}

function ensureModalOverlayStyling(svg) {
  if (!svg) return
  if (!svg.hasAttribute('data-zoom-overlay-active')) svg.setAttribute('data-zoom-overlay-active', 'true')
  // Inject a scoped style once to hide base layers when overlay is present
  let style = svg.querySelector('style[data-slp-overlay-style]')
  if (!style) {
    style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
    style.setAttribute('data-slp-overlay-style', 'true')
    // Hide all immediate children except our overlay and t0 marker (so we don't double-draw)
    style.textContent = `
      [data-zoom-overlay-active="true"] > :not([data-zoom-overlay]):not([data-t0-marker]) { opacity: 0; }
    `
    svg.insertBefore(style, svg.firstChild)
  }
}

function smoothScrollToY(targetY, duration = 1200) {
  const startY = window.scrollY || window.pageYOffset
  const delta = Math.max(0, Math.round(targetY - startY))
  const start = performance.now()
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
  function step(now) {
    const elapsed = now - start
    const t = Math.min(1, elapsed / duration)
    const eased = easeInOutCubic(t)
    window.scrollTo(0, Math.round(startY + delta * eased))
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

function ensureHintStyles() {
  if (document.querySelector('style[data-slp-hint]')) return
  const style = document.createElement('style')
  style.setAttribute('data-slp-hint', 'true')
  style.textContent = `@keyframes slp-hint-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}`
  document.head.appendChild(style)
}

function hideExternalHint(plotEl) {
  const col = plotEl.closest('.slp-preview-plot-column') || plotEl.parentElement
  if (!col) return
  const tip = col.querySelector('[data-role="drag-hint"]')
  if (tip) tip.remove()
}

function showExternalHint(plotEl, slug = '') {
  ensureHintStyles()
  const col = plotEl.closest('.slp-preview-plot-column') || plotEl.parentElement
  if (!col) return
  const btn = col.querySelector('.slp-preview-enlarge')
  if (!btn) return
  col.style.position = col.style.position || 'relative'
  // Remove any existing
  hideExternalHint(plotEl)
  // Create a simple persistent bubble
  const tip = document.createElement('div')
  tip.setAttribute('data-role', 'drag-hint')
  tip.setAttribute('role', 'status')
  tip.setAttribute('aria-live', 'polite')
  tip.textContent = 'Drag the t₀ marker'
  Object.assign(tip.style, {
    position: 'absolute',
    zIndex: '40',
    background: 'var(--wa-color-neutral-900, #2463eb)',
    color: 'white',
    fontSize: '12px',
    lineHeight: '1',
    padding: '8px 10px',
    borderRadius: '6px',
    boxShadow: 'rgb(122 123 152 / 78%) 1px 2px 4px',
    whiteSpace: 'nowrap',
    pointerEvents: 'auto',
    transform: 'translateY(0)',
    animation: 'slp-hint-bounce 1.2s ease-in-out infinite',
  })
  // Arrow: short SVG triangle, pointing UP, placed just below bubble
  const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  arrow.setAttribute('width', '14')
  arrow.setAttribute('height', '8')
  arrow.setAttribute('viewBox', '0 0 14 8')
  Object.assign(arrow.style, {
    position: 'absolute',
    bottom: '27px',
    left: '74px',
  })
  const tri = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  // Upward triangle: base along bottom (y=8), tip at y=0
  tri.setAttribute('d', 'M0,8 L7,0 L14,8 Z')
  tri.setAttribute('fill', 'var(--wa-color-neutral-900, #2463eb)')
  arrow.appendChild(tri)
  tip.appendChild(arrow)

  // Position above the button
  const btnRect = btn.getBoundingClientRect()
  const colRect = col.getBoundingClientRect()
  const top = btnRect.top - colRect.top - 44
  const left = btnRect.left - colRect.left + 8
  tip.style.top = `${Math.max(0, Math.round(top))}px`
  tip.style.left = `${Math.max(0, Math.round(left))}px`

  col.appendChild(tip)
  // After insertion, align arrow near the right side of the bubble (roughly above the 'k')
  requestAnimationFrame(() => {
    const w = tip.offsetWidth || 0
    const desired = Math.max(8, Math.round(w * 0.72))
    arrow.style.left = `${desired}px`
  })
  // Hide on interactions
  const hide = () => hideExternalHint(plotEl)
  tip.addEventListener('click', hide, { once: true })
  btn.addEventListener('click', hide, { once: true })
}

function showDragHint(plotEl, svg, handle, slug = '') {
  // Create target overlay anchored to handle position
  const rect = handle.getBoundingClientRect()
  const hostRect = plotEl.getBoundingClientRect()
  const targetId = `drag-hint-${slug || Math.random().toString(36).slice(2)}`
  let target = plotEl.querySelector(`#${CSS.escape(targetId)}`)
  if (!target) {
    target = document.createElement('div')
    target.id = targetId
    target.setAttribute('aria-hidden', 'true')
    target.style.position = 'absolute'
    target.style.width = '20px'
    target.style.height = '16px'
    target.style.pointerEvents = 'none'
    plotEl.appendChild(target)
  }
  // Position slightly above the handle
  const left = rect.left - hostRect.left
  const top = rect.top - hostRect.top - 28
  target.style.left = `${Math.round(left)}px`
  target.style.top = `${Math.round(top)}px`

  // Create tooltip if not present
  // No wa-tooltip: external custom hint covers onboarding
}

function hideDragHint(plotEl, slug = '') {
  const idPrefix = slug ? `drag-hint-${slug}` : 'drag-hint-'
  plotEl.querySelectorAll(`[id^="${idPrefix}"]`).forEach((el) => el.remove())
  try {
    if (slug) sessionStorage.setItem(`slp.dragHint.${slug}`, '1')
  } catch (_e) { /* ignore */ }
}

document.addEventListener("DOMContentLoaded", () => {
  const root = document.documentElement
  const form = document.getElementById("slp-form")
  const dropzone = document.querySelector("[data-dropzone]")
  const fileInput = dropzone?.querySelector('input[type="file"]') ?? null
  const summaryEl = dropzone?.querySelector("[data-dropzone-summary]") ?? null
  const clearBtn = dropzone?.querySelector("[data-dropzone-clear]") ?? null
  const summaryLabelEl = dropzone?.querySelector("[data-dropzone-summary-label]") ?? null
  const fileListEl = dropzone?.querySelector("[data-dropzone-list]") ?? null
  const fileListLabelEl = dropzone?.querySelector("[data-dropzone-list-label]") ?? null
  const previewSection = document.querySelector(".slp-column--preview")
  const previewTarget = document.getElementById("slp-preview")
  const paramsSection = document.querySelector(".slp-column--params")
  const resultsSection = document.querySelector(".slp-column--results")
  const defaultSummary = summaryEl?.textContent ?? "Awaiting file…"
  const previewUrl = form?.dataset.previewUrl ?? form?.getAttribute("data-preview-url") ?? null
  const themeToggle = document.querySelector("[data-theme-toggle]")
  const resultRegion = document.getElementById("slp-result")
  const stepBadges = {
    upload: document.querySelector('[data-step="1"]'),
    preview: document.querySelector('[data-step="2"]'),
    params: document.querySelector('[data-step="3"]'),
    results: document.querySelector('[data-step="4"]'),
  }
  let selectedFiles = []
  let previewConfirmed = false

  const updateStepBadges = (activeKey) => {
    Object.entries(stepBadges).forEach(([key, badge]) => {
      if (!badge) return
      const display = key === activeKey ? "inline-flex" : "none"
      badge.style.setProperty("display", display, "important")
    })
  }

  const previewPlaceholder = `
    <p class="placeholder">
      Upload a dataset to preview raw curves and choose t₀ values before running the analysis.
    </p>
  `

  const resultPlaceholder = `
    <p class="placeholder">
      Submit a dataset to see the Phenomenological Analysis Method (PAM) output here.
      The response will include SLP values, diagnostics, and an inline SVG plot.
    </p>
  `

  const previewLoadingMarkup = `<p class="placeholder">Loading dataset preview…</p>`

  const renderPreviewError = (message) => `
    <div class="slp-error" data-role="slp-error">
      <h3>Preview failed</h3>
      <p>${message}</p>
    </div>
  `

  const showPanel = (panel) => {
    if (panel) panel.removeAttribute("hidden")
  }

  const hidePanel = (panel) => {
    if (panel) panel.setAttribute("hidden", "hidden")
  }

  const resetResults = () => {
    if (!resultRegion) return
    resultRegion.dataset.state = "idle"
    resultRegion.classList.remove("slp-result--filled")
    resultRegion.innerHTML = resultPlaceholder
  }

  const resetWorkflowPanels = () => {
    hidePanel(previewSection)
    hidePanel(paramsSection)
    hidePanel(resultsSection)
  }

  const scrollToPanel = (panel, offset = 120, duration = 1800) => {
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    const currentY = window.scrollY || window.pageYOffset
    const targetY = Math.max(0, Math.round(currentY + rect.top - offset))
    smoothScrollToY(targetY, duration)
  }

  updateStepBadges("upload")
  resetWorkflowPanels()

  const t0Selections = new Map()
  let previewModal = null
  let previewModalContent = null
  const conversionErrorAttr = 'data-preview-conversion-error'

  const renderFileList = (files = []) => {
    if (!fileListEl) return
    fileListEl.innerHTML = ""

    if (!files.length) {
      const emptyItem = document.createElement("li")
      emptyItem.className = "slp-dropzone__file-item"
      emptyItem.dataset.empty = "true"
      emptyItem.textContent = "No dataset yet."
      fileListEl.appendChild(emptyItem)
      return
    }

    files.forEach((file) => {
      const item = document.createElement("li")
      item.className = "slp-dropzone__file-item"
      item.textContent = file.name
      item.title = `${file.name} (${Math.round(file.size / 1024)} KB)`
      fileListEl.appendChild(item)
    })
  }

  const updateDropzoneState = (files) => {
    if (!dropzone || !summaryEl) return
    const fileArray = Array.from(files ?? [])
    const hasFiles = fileArray.length > 0

    if (hasFiles) {
      summaryLabelEl?.removeAttribute("hidden")
      const [first, ...rest] = fileArray
      const suffix = rest.length ? ` (+${rest.length})` : ""
      summaryEl.textContent = `${first.name}${suffix}`
      dropzone.dataset.state = "filled"
      clearBtn?.removeAttribute("hidden")
      fileListLabelEl?.removeAttribute("hidden")
    } else {
      summaryLabelEl?.setAttribute("hidden", "hidden")
      summaryEl.textContent = defaultSummary
      dropzone.dataset.state = "empty"
      clearBtn?.setAttribute("hidden", "hidden")
      fileListLabelEl?.setAttribute("hidden", "hidden")
    }

    renderFileList(fileArray)
  }

  const replaceSelectedFiles = (incoming = []) => {
    const fileArray = Array.from(incoming ?? [])
    selectedFiles = fileArray.length ? [fileArray[fileArray.length - 1]] : []
    previewConfirmed = false
    t0Selections.clear()
    if (previewTarget) {
      delete previewTarget.dataset.scrolledOnce
    }
    if (resultsSection) {
      delete resultsSection.dataset.scrolledOnce
    }
    hidePanel(paramsSection)
    hidePanel(resultsSection)
    resetResults()
    form?.classList.remove("slp-form--validated")
  }

  const setInputFiles = () => {
    if (!fileInput) return
    if (!selectedFiles.length) {
      fileInput.value = ""
      return
    }
    try {
      const dt = new DataTransfer()
      selectedFiles.forEach((file) => dt.items.add(file))
      fileInput.files = dt.files
    } catch (_error) {
      selectedFiles = selectedFiles.slice(-1)
      try {
        const dt = new DataTransfer()
        dt.items.add(selectedFiles[0])
        fileInput.files = dt.files
      } catch (__error) {
        /* no-op */
      }
    }
  }

  const populateT0Selections = () => {
    if (!previewTarget) return
    t0Selections.clear()
    previewTarget.querySelectorAll('input[name^="t0_seconds["]').forEach((input) => {
      const match = input.name.match(/^t0_seconds\[(.+)\]$/)
      if (!match) return
      const value = parseFloat(input.value)
      if (!Number.isNaN(value)) {
        t0Selections.set(match[1], value)
      }
    })
  }

  const sanitizeDatasetSlug = (value, fallback) => {
    if (!value) return fallback
    let sanitized = value
      .toString()
      .trim()
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/^\.+|\.+$/g, '')

    const lower = sanitized.toLowerCase()
    for (const ext of ['.svg', '.csv', '.txt']) {
      if (lower.endsWith(ext)) {
        sanitized = sanitized.slice(0, -ext.length)
        break
      }
    }

    sanitized = sanitized.replace(/^\.+|\.+$/g, '')
    return sanitized || fallback
  }

  const buildCsvFilesFromPreview = () => {
    if (!previewTarget) return []
    const cards = previewTarget.querySelectorAll('.slp-preview-card')
    if (!cards.length) return []
    const files = []

    cards.forEach((card, index) => {
      const plot = card.querySelector('.slp-preview-plot')
      if (!plot) return
      const pointsAttr = plot.getAttribute('data-points')
      if (!pointsAttr) return
      let points
      try {
        points = JSON.parse(pointsAttr)
      } catch (error) {
        console.error('Failed to parse preview points', error)
        return
      }
      if (!Array.isArray(points) || !points.length) return

      const headerEl = card.querySelector('header h3')
      const label = headerEl?.textContent?.trim() || `Dataset ${index + 1}`
      const slug = sanitizeDatasetSlug(plot.getAttribute('data-slug'), `dataset-${index + 1}`)
      const csvLines = points
        .map((pair) => {
          const [time, temp] = Array.isArray(pair) ? pair : []
          if (Number.isFinite(time) && Number.isFinite(temp)) {
            return `${time},${temp}`
          }
          const parsedTime = Number(time)
          const parsedTemp = Number(temp)
          if (!Number.isNaN(parsedTime) && !Number.isNaN(parsedTemp)) {
            return `${parsedTime},${parsedTemp}`
          }
          return ''
        })
        .filter(Boolean)

      if (!csvLines.length) return

      const csv = `Time,Temperature\n${csvLines.join('\n')}\n`
      const fileName = `${slug}.csv`
      try {
        const file = new File([csv], fileName, {
          type: 'text/csv',
          lastModified: Date.now()
        })
        files.push(file)
      } catch (error) {
        console.error('Failed to create CSV file from preview', error)
      }
    })

    return files
  }

  const showPreviewConversionError = (message) => {
    if (!previewTarget) return
    previewTarget.querySelectorAll(`[${conversionErrorAttr}]`).forEach((el) => el.remove())
    const wrapper = document.createElement('div')
    wrapper.innerHTML = renderPreviewError(message)
    const errorEl = wrapper.firstElementChild
    if (!errorEl) return
    errorEl.setAttribute(conversionErrorAttr, 'true')
    previewTarget.prepend(errorEl)
    window.setTimeout(() => {
      errorEl.remove()
    }, 5000)
  }

  const requestPreview = () => {
    if (!previewTarget || !previewUrl) return
    if (!selectedFiles.length) {
      previewTarget.dataset.state = "idle"
      previewTarget.innerHTML = previewPlaceholder
      updateStepBadges("upload")
      hidePanel(previewSection)
      previewModal = null
      previewModalContent = null
      return
    }

    showPanel(previewSection)

    const formData = new FormData()
    selectedFiles.forEach((file) => {
      formData.append("datasets", file, file.name)
    })

    previewTarget.dataset.state = "loading"
    previewTarget.innerHTML = previewLoadingMarkup

    fetch(previewUrl, {
      method: "POST",
      body: formData,
    })
      .then(async (response) => {
        const text = await response.text()
        if (!response.ok) {
          throw new Error(text)
        }
        return text
      })
      .then((html) => {
        previewTarget.innerHTML = html
        previewTarget.dataset.state = "ready"
        previewModal = previewTarget.querySelector("[data-preview-modal]")
        previewModalContent = previewModal?.querySelector("[data-preview-modal-content]") ?? null
        populateT0Selections()
        // Attach interactions for draggable t0 markers
        previewTarget.querySelectorAll('.slp-preview-plot').forEach((plot) => {
          attachPreviewInteractions(plot)
        })
        // Smooth-scroll to Step 2 preview on first render for this selection.
        if (!previewTarget.dataset.scrolledOnce) {
          previewTarget.dataset.scrolledOnce = "1"
          scrollToPanel(previewSection)
        }
        updateStepBadges("preview")
      })
      .catch((error) => {
        previewTarget.dataset.state = "error"
        const message = (error.message || "").trim()
        if (message.startsWith("<")) {
          previewTarget.innerHTML = message
        } else {
          previewTarget.innerHTML = renderPreviewError(message || "Unable to generate preview.")
        }
        previewModal = null
        previewModalContent = null
        updateStepBadges("preview")
        if (!previewTarget.dataset.scrolledOnce) {
          previewTarget.dataset.scrolledOnce = "1"
          scrollToPanel(previewSection)
        }
      })
  }

  const applySelectedFiles = () => {
    setInputFiles()
    updateDropzoneState(selectedFiles)
    if (selectedFiles.length) {
      showPanel(previewSection)
      hidePanel(paramsSection)
      hidePanel(resultsSection)
      updateStepBadges("preview")
    } else {
      resetWorkflowPanels()
      updateStepBadges("upload")
    }
    requestPreview()
  }

  const clearSelectedFiles = () => {
    selectedFiles = []
    t0Selections.clear()
    previewConfirmed = false
    resetResults()
    if (resultsSection) {
      delete resultsSection.dataset.scrolledOnce
    }
    form?.classList.remove("slp-form--validated")
    applySelectedFiles()
  }

  if (dropzone && fileInput) {

    const assignFilesToInput = (files) => {
      const incoming = Array.from(files ?? [])
      if (!incoming.length) {
        clearSelectedFiles()
        return
      }
      replaceSelectedFiles(incoming)
      applySelectedFiles()
    }

      ;["dragenter", "dragover"].forEach((type) => {
        dropzone.addEventListener(type, (event) => {
          event.preventDefault()
          dropzone.classList.add("is-dragover")
        })
      })

    dropzone.addEventListener("dragleave", (event) => {
      if (!dropzone.contains(event.relatedTarget)) {
        dropzone.classList.remove("is-dragover")
      }
    })

    dropzone.addEventListener("dragend", () => {
      dropzone.classList.remove("is-dragover")
    })

    dropzone.addEventListener("drop", (event) => {
      event.preventDefault()
      dropzone.classList.remove("is-dragover")
      if (event.dataTransfer?.files?.length) {
        assignFilesToInput(event.dataTransfer.files)
      }
    })

    fileInput.addEventListener("change", (event) => {
      const freshFiles = Array.from(event.target.files ?? [])
      if (freshFiles.length) {
        replaceSelectedFiles(freshFiles)
      } else {
        clearSelectedFiles()
        return
      }
      applySelectedFiles()
    })

    fileInput.addEventListener("focus", () => dropzone.classList.add("is-focused"))
    fileInput.addEventListener("blur", () => dropzone.classList.remove("is-focused"))

    dropzone.addEventListener("click", (event) => {
      if ((event.target).matches && event.target.matches("[data-dropzone-clear]")) {
        event.preventDefault()
        clearSelectedFiles()
      }
    })

    if (form) {
      form.addEventListener("reset", () => {
        window.setTimeout(() => {
          clearSelectedFiles()
          form.classList.remove("slp-form--validated")
          resetResults()

          window.scrollTo({
            top: 0,
            behavior: "smooth"
          })
        }, 0)
      })
    }

    updateDropzoneState(fileInput.files)
    if (previewTarget && !previewTarget.innerHTML.trim()) {
      previewTarget.innerHTML = previewPlaceholder
    }
    if (selectedFiles.length) {
      requestPreview()
    }
  }

  if (previewTarget) {
    previewTarget.addEventListener("input", (event) => {
      const target = event.target
      if (!(target instanceof HTMLInputElement)) return
      const match = target.name.match(/^t0_seconds\[(.+)\]$/)
      if (!match) return
      const slug = match[1]
      const value = parseFloat(target.value)
      if (!Number.isNaN(value)) {
        t0Selections.set(slug, value)
        // Update the main preview plot marker to match the input
        const mainPlot = previewTarget.querySelector(`.slp-preview-plot[data-slug="${CSS.escape(slug)}"]`)
        if (mainPlot) {
          updatePlotMarkerVisuals(mainPlot, value)
        }
      }
    })

    previewTarget.addEventListener("click", (event) => {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (target.matches("[data-preview-modal-close]")) {
        if (previewModal) {
          previewModal.setAttribute("hidden", "hidden")
          previewModalContent?.replaceChildren()
        }
        return
      }

      const enlargeBtn = target.closest("[data-action='enlarge-preview']")
      if (enlargeBtn) {
        if (!previewModal || !previewModalContent) return
        const rawTitle = enlargeBtn.getAttribute("data-preview-title") ?? "Dataset preview"
        const title = rawTitle.length > 25 ? rawTitle.substring(0, 25) + '…' : rawTitle
        const svgMarkup = enlargeBtn.getAttribute("data-preview-svg") ?? ""
        // Find the originating plot to copy dataset metadata
        const originPlot = enlargeBtn.closest('.slp-preview-plot-column')?.querySelector('.slp-preview-plot')
        const points = originPlot?.getAttribute('data-points') ?? '[]'
        const slug = originPlot?.getAttribute('data-slug') ?? ''
        // Wrap SVG so interactions in modal can reuse the same logic (add Reset view in modal)
        previewModalContent.innerHTML = `
          <div class="slp-modal-header">
            <button type="button" class="btn-secondary" data-action="reset-zoom">
              <wa-icon name="magnifying-glass-minus" class="btn-icon"></wa-icon>
              <span class="btn-text">Reset view</span>
            </button>
            <h4>${title}</h4>
            <div aria-hidden="true"></div>
          </div>
          <div class="slp-preview-plot" data-chart="preview" data-slug="${slug}" data-points='${points}'>
            ${svgMarkup}
          </div>
          <div class="slp-modal-footer">
            <p class="slp-modal-footer__text">
              <span class="slp-modal-footer__label">Help</span>
              <span class="slp-modal-footer__hint"><span class="slp-key">Click</span> to zoom in</span>
              <span class="slp-modal-footer__hint"><span class="slp-key">Shift</span> + <span class="slp-key">Click</span> to zoom out</span>
              <span class="slp-modal-footer__hint"><span class="slp-key">Drag</span> the handle to adjust t₀, then close the window</span>
            </p>
          </div>
        `
        const modalPlot = previewModalContent.querySelector('.slp-preview-plot')
        if (modalPlot) {
          attachPreviewInteractions(modalPlot)
          attachModalZoom(modalPlot)
        }
        previewModal.removeAttribute("hidden")
        return
      }

      if (target.matches("[data-action='confirm-preview']")) {
        event.preventDefault()
        populateT0Selections()
        const csvFiles = buildCsvFilesFromPreview()
        if (!csvFiles.length) {
          showPreviewConversionError("Could not read the preview data. Please re-upload the datasets and try again.")
          return
        }
        selectedFiles = csvFiles
        setInputFiles()
        updateDropzoneState(selectedFiles)
        previewConfirmed = true
        showPanel(paramsSection)
        hidePanel(resultsSection)
        updateStepBadges("params")

        setTimeout(() => {
          scrollToPanel(paramsSection)
        }, 150)
      }
    })
  }

  console.log("Form found:", !!form, "Result region found:", !!resultRegion)

  // Add MutationObserver to watch for results being added
  if (resultRegion) {
    const observer = new MutationObserver((mutations) => {
      // Check if results were added (not just loading state)
      const hasContent = resultRegion.innerHTML.trim().length > 200
      const hasPlaceholder = resultRegion.querySelector(".placeholder")
      const hasError = resultRegion.querySelector(".slp-error")
      const isLoading = resultRegion.dataset.state === "loading"

      // Only trigger if we have real results (not loading, not placeholder, not error)
      if (hasContent && !hasPlaceholder && !hasError && !isLoading) {
        console.log("MutationObserver detected results loaded")
        showPanel(resultsSection)
        updateStepBadges("results")

        // Add Reset button next to Download SVG button
        // First, try to find existing .slp-result-actions
        let resultActions = resultRegion.querySelector(".slp-result-actions")
        console.log("resultActions found:", !!resultActions)

        if (!resultActions) {
          // If no container exists, find the download form/button and create a wrapper
          const downloadForm = resultRegion.querySelector(".slp-download-form") ||
            resultRegion.querySelector("form[action*='download']")
          const downloadBtn = resultRegion.querySelector(".slp-download")

          console.log("downloadForm found:", !!downloadForm)
          console.log("downloadBtn found:", !!downloadBtn)

          if (downloadForm || downloadBtn) {
            // Create the actions container
            resultActions = document.createElement("div")
            resultActions.className = "slp-result-actions"

            const target = downloadForm || downloadBtn
            // Insert the container before the download element
            target.parentNode.insertBefore(resultActions, target)
            // Move the download element into the container
            resultActions.appendChild(target)
            console.log("Created .slp-result-actions wrapper")
          }
        }

        // Now add the Reset button if we have a container
        if (resultActions && !resultActions.querySelector(".btn-reset-results")) {
          const resetBtn = document.createElement("button")
          resetBtn.className = "btn-secondary btn-reset-results"
          resetBtn.type = "button"
          resetBtn.textContent = "Reset"
          resetBtn.addEventListener("click", () => {
            if (form) {
              form.reset()
            }
          })
          resultActions.appendChild(resetBtn)
          console.log("Reset button added to results")
        }
        // Scroll to the Results section
        if (!resultsSection?.dataset.scrolledOnce) {
          setTimeout(() => {
            scrollToPanel(resultsSection)
            if (resultsSection) {
              resultsSection.dataset.scrolledOnce = "1"
            }
          }, 500)
        }
      }
    })

    observer.observe(resultRegion, {
      childList: true,
      subtree: true
    })
  }

  if (form && resultRegion) {
    console.log("Setting up form event listeners")
    const submitBtn = document.getElementById("slp-submit")
    const submitIcon = submitBtn?.querySelector(".btn-icon")
    const submitText = submitBtn?.querySelector(".btn-text")

    form.addEventListener("submit", (event) => {
      if (!previewConfirmed) {
        event.preventDefault()
        event.stopImmediatePropagation()
        showPanel(previewSection)
        hidePanel(paramsSection)
        hidePanel(resultsSection)
        updateStepBadges("preview")
        scrollToPanel(previewSection)
        return
      }

      if (!form.reportValidity()) {
        event.preventDefault()
        event.stopImmediatePropagation()
        form.classList.add("slp-form--validated")
      }
    })

    form.addEventListener("input", () => {
      if (form.classList.contains("slp-form--validated") && form.checkValidity()) {
        form.classList.remove("slp-form--validated")
      }
    })

    // Inject t₀ values from Step 2 as hidden inputs before form submission
    form.addEventListener("htmx:configRequest", (event) => {
      // Ensure files are still in the input
      if (selectedFiles.length && fileInput) {
        setInputFiles()
      }

      // Remove any existing t0_seconds hidden inputs (but NOT the visible ones in Step 2 preview)
      form.querySelectorAll('input[name^="t0_seconds"][type="hidden"]').forEach(input => input.remove())

      // Add hidden inputs for each t₀ selection from Step 2
      t0Selections.forEach((value, slug) => {
        const hiddenInput = document.createElement('input')
        hiddenInput.type = 'hidden'
        hiddenInput.name = `t0_seconds[${slug}]`
        hiddenInput.value = String(value)
        form.appendChild(hiddenInput)
      })
    })

    form.addEventListener("htmx:beforeRequest", (event) => {
      if (!previewConfirmed) {
        showPanel(previewSection)
        hidePanel(paramsSection)
        hidePanel(resultsSection)
        updateStepBadges("preview")
        scrollToPanel(previewSection)
        event.preventDefault()
        return
      }

      if (!form.checkValidity()) {
        form.classList.add("slp-form--validated")
        form.reportValidity()
        event.preventDefault()
        return
      }

      showPanel(resultsSection)
      updateStepBadges("results")
      scrollToPanel(resultsSection)
      if (resultsSection) {
        resultsSection.dataset.scrolledOnce = "1"
      }

      resultRegion.dataset.state = "loading"
      resultRegion.setAttribute("aria-busy", "true")
      resultRegion.innerHTML = `
        <p class="placeholder">Results will appear here...</p>
      `

      // Show loading message in Step 3
      const loadingMessage = document.getElementById("slp-loading-message")
      if (loadingMessage) {
        loadingMessage.style.display = "block"
      }

      // Animate submit button
      if (submitBtn) {
        submitBtn.dataset.loading = "true"
        if (submitText) submitText.textContent = "Loading"

        // Wait for animation to complete
        setTimeout(() => {
          submitBtn.dataset.loading = "false"
          submitBtn.dataset.state = "success"
          if (submitIcon) submitIcon.setAttribute("name", "circle-check")
          if (submitText) submitText.textContent = "Done"

          // Reset after 2 seconds
          setTimeout(() => {
            submitBtn.dataset.state = "idle"
            if (submitIcon) submitIcon.setAttribute("name", "upload")
            if (submitText) submitText.textContent = "Run SLP analysis"
          }, 2000)
        }, 2500)
      }
    })

    form.addEventListener("htmx:afterSwap", (event) => {
      console.log("htmx:afterSwap fired")
      if (event.detail.target !== resultRegion) return
      const role = resultRegion.querySelector("[data-role]")?.dataset.role ?? "idle"
      console.log("Result role:", role)
      showPanel(resultsSection)

      // Check if we have actual results (not just placeholder)
      const hasResults = resultRegion.innerHTML.trim().length > 100 && !resultRegion.querySelector(".placeholder")
      console.log("Has results:", hasResults)

      if (role === "slp-success" || hasResults) {
        resultRegion.dataset.state = "success"
        resultRegion.classList.add("slp-result--filled")
        updateStepBadges("results")
        console.log("Step 3 badge should be visible now")
      } else if (role === "slp-error") {
        resultRegion.dataset.state = "error"
        resultRegion.classList.add("slp-result--filled")
        updateStepBadges("results")
      } else {
        resultRegion.dataset.state = "idle"
        resultRegion.classList.remove("slp-result--filled")
      }
      resultRegion.removeAttribute("aria-busy")
      
      // Hide loading message in Step 3
      const loadingMessage = document.getElementById("slp-loading-message")
      if (loadingMessage) {
        loadingMessage.style.display = "none"
      }
    })

    form.addEventListener("htmx:afterRequest", () => {
      if (resultRegion.dataset.state === "loading") {
        resultRegion.dataset.state = "idle"
        resultRegion.classList.remove("slp-result--filled")
        resultRegion.removeAttribute("aria-busy")
        if (submitBtn) {
          submitBtn.dataset.loading = "false"
          submitBtn.dataset.state = "idle"
          if (submitIcon) submitIcon.setAttribute("name", "upload")
          if (submitText) submitText.textContent = "Run SLP analysis"
        }
      }
      
      // Hide loading message in Step 3
      const loadingMessage = document.getElementById("slp-loading-message")
      if (loadingMessage) {
        loadingMessage.style.display = "none"
      }
    })

    form.addEventListener("htmx:responseError", () => {
      showPanel(resultsSection)
      updateStepBadges("results")
      resultRegion.dataset.state = "error"
      resultRegion.innerHTML = `
        <div class="slp-error" data-role="slp-error">
          <h3>Request failed</h3>
          <p>Unable to reach the analysis service. Please try again.</p>
        </div>
      `
      
      // Hide loading message in Step 3
      const loadingMessage = document.getElementById("slp-loading-message")
      if (loadingMessage) {
        loadingMessage.style.display = "none"
      }
      resultRegion.removeAttribute("aria-busy")
      if (submitBtn) {
        submitBtn.dataset.loading = "false"
        submitBtn.dataset.state = "idle"
        if (submitIcon) submitIcon.setAttribute("name", "upload")
        if (submitText) submitText.textContent = "Run SLP analysis"
      }
    })
  }

  if (themeToggle) {
    const themeButtons = themeToggle.querySelectorAll(".slp-theme-toggle__btn")
    const heroSection = document.querySelector(".slp-hero")

    const applyTheme = (state) => {
      const nextState = state === "light" || state === "dark" ? state : "auto"
      if (nextState === "light" || nextState === "dark") {
        root.setAttribute("data-theme", nextState)
      } else {
        root.removeAttribute("data-theme")
      }
      themeToggle.dataset.themeState = nextState

      // Force update background images and colors
      if (heroSection) {
        if (nextState === "dark") {
          heroSection.style.backgroundImage = "url('/images/bg-dark.svg')"
          document.body.style.backgroundColor = "#0d1320"
        } else if (nextState === "light") {
          heroSection.style.backgroundImage = "url('/images/bg.svg')"
          document.body.style.backgroundColor = "#bdd4e7"
        } else {
          // Auto mode - remove inline styles to let CSS media queries handle it
          heroSection.style.backgroundImage = ""
          document.body.style.backgroundColor = ""
        }
      }

      return nextState
    }

    let storedTheme = null
    try {
      storedTheme = localStorage.getItem(THEME_STORAGE_KEY)
    } catch (_error) {
      storedTheme = null
    }
    const initial = applyTheme(storedTheme)

    themeButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const theme = btn.dataset.theme
        const resolved = applyTheme(theme)
        try {
          if (resolved === "auto") {
            localStorage.removeItem(THEME_STORAGE_KEY)
          } else {
            localStorage.setItem(THEME_STORAGE_KEY, resolved)
          }
        } catch (_error) {
          /* no-op when storage unavailable */
        }
      })
    })
  }

  console.info("Bridgetown UI bootstrap loaded.")
})

// --- Helper to update plot marker visuals (shared by drag and input sync) ---
function updatePlotMarkerVisuals(plotEl, tVal, options = {}) {
  if (!plotEl) return null
  const svg = plotEl.querySelector('[data-chart="preview-svg"]')
  const t0Group = svg?.querySelector('[data-t0-marker]')
  const handle = svg?.querySelector('[data-t0-handle]')
  if (!svg || !t0Group) return null

  const pointsAttr = plotEl.getAttribute('data-points') || '[]'
  let points = []
  try { points = JSON.parse(pointsAttr) } catch (_e) { points = [] }
  if (!Array.isArray(points) || points.length === 0) return null

  const width = parseFloat(svg.getAttribute('data-width') || '480')
  const height = parseFloat(svg.getAttribute('data-height') || '260')
  const marginLeft = parseFloat(svg.getAttribute('data-margin-left') || '64')
  const marginRight = parseFloat(svg.getAttribute('data-margin-right') || '24')
  const marginTop = parseFloat(svg.getAttribute('data-margin-top') || '24')
  const marginBottom = parseFloat(svg.getAttribute('data-margin-bottom') || '56')
  const minTime = parseFloat(svg.getAttribute('data-min-time') || '0')
  const maxTime = parseFloat(svg.getAttribute('data-max-time') || '1')
  const minTemp = parseFloat(svg.getAttribute('data-min-temp') || '0')
  const maxTemp = parseFloat(svg.getAttribute('data-max-temp') || '1')

  const usableWidth = width - marginLeft - marginRight
  const scaleX = (t) => marginLeft + ((t - minTime) / Math.max(maxTime - minTime, 1e-9)) * usableWidth
  const scaleY = (temp) => {
    const usableHeight = height - marginTop - marginBottom
    const ratio = (temp - minTemp) / Math.max(maxTemp - minTemp, 1e-9)
    return height - marginBottom - ratio * usableHeight
  }

  const nearestPointByTime = (t) => {
    let best = points[0]
    let bestDist = Math.abs(best[0] - t)
    for (let i = 1; i < points.length; i++) {
      const d = Math.abs(points[i][0] - t)
      if (d < bestDist) { best = points[i]; bestDist = d }
    }
    return best
  }

  const [snapT, snapY] = nearestPointByTime(tVal)
  const cx = scaleX(snapT)
  const cy = scaleY(snapY)

  // Update visuals
  const line = t0Group.querySelector('line')
  const circle = t0Group.querySelector('circle')
  const readout = t0Group.querySelector('[data-t0-readout]')

  if (line) {
    line.setAttribute('x1', cx.toFixed(2))
    line.setAttribute('x2', cx.toFixed(2))
  }
  if (circle) {
    circle.setAttribute('cx', cx.toFixed(2))
    circle.setAttribute('cy', cy.toFixed(2))
  }
  if (handle) {
    const w = parseFloat(handle.getAttribute('data-width') || handle.getAttribute('width') || '20')
    const tag = handle.tagName.toLowerCase()
    if (tag === 'g') {
      const current = handle.getAttribute('transform') || 'translate(0,0)'
      const match = current.match(/translate\(([-\d\.]+)\s*,\s*([\-\d\.]+)\)/)
      const ty = match ? parseFloat(match[2]) : 0
      const tx = cx - w / 2
      handle.setAttribute('transform', `translate(${tx.toFixed(2)},${ty.toFixed(2)})`)
    } else {
      handle.setAttribute('x', (cx - w / 2).toFixed(2))
    }
    const title = handle.querySelector && handle.querySelector('title')
    if (title) title.textContent = `t₀: ${snapT.toFixed(3)} s (drag to adjust)`
  }
  t0Group.setAttribute('data-t0-seconds', String(snapT))
  if (readout) {
    readout.textContent = `t₀: ${snapT.toFixed(3)} s`
    const rx = cx + 8
    const ry = marginTop + 16
    readout.setAttribute('x', rx.toFixed(2))
    readout.setAttribute('y', ry.toFixed(2))
  }

  // Return snapped value for caller to use (e.g., to update input)
  return snapT
}

// --- Draggable t0 interactions ---
function attachPreviewInteractions(plotEl) {
  if (!plotEl) return
  const svg = plotEl.querySelector('[data-chart="preview-svg"]')
  const t0Group = svg?.querySelector('[data-t0-marker]')
  const handle = svg?.querySelector('[data-t0-handle]')
  if (!svg || !t0Group || !handle) return

  const pointsAttr = plotEl.getAttribute('data-points') || '[]'
  let points = []
  try { points = JSON.parse(pointsAttr) } catch (_e) { points = [] }
  if (!Array.isArray(points) || points.length === 0) return

  const slug = plotEl.getAttribute('data-slug') || ''
  const findInput = () => document.querySelector(`input[name="t0_seconds[${CSS.escape(slug)}]"]`)

  const width = parseFloat(svg.getAttribute('data-width') || '480')
  const height = parseFloat(svg.getAttribute('data-height') || '260')
  const marginLeft = parseFloat(svg.getAttribute('data-margin-left') || '64')
  const marginRight = parseFloat(svg.getAttribute('data-margin-right') || '24')
  const marginTop = parseFloat(svg.getAttribute('data-margin-top') || '24')
  const marginBottom = parseFloat(svg.getAttribute('data-margin-bottom') || '56')
  const minTime = parseFloat(svg.getAttribute('data-min-time') || '0')
  const maxTime = parseFloat(svg.getAttribute('data-max-time') || '1')
  const minTemp = parseFloat(svg.getAttribute('data-min-temp') || '0')
  const maxTemp = parseFloat(svg.getAttribute('data-max-temp') || '1')

  const usableWidth = width - marginLeft - marginRight

  const scaleX = (t) => marginLeft + ((t - minTime) / Math.max(maxTime - minTime, 1e-9)) * usableWidth
  const invertX = (x) => {
    const clamped = Math.min(Math.max(x, marginLeft), width - marginRight)
    const ratio = (clamped - marginLeft) / Math.max(usableWidth, 1e-9)
    return minTime + ratio * (maxTime - minTime)
  }

  const scaleY = (temp) => {
    const usableHeight = height - marginTop - marginBottom
    const ratio = (temp - minTemp) / Math.max(maxTemp - minTemp, 1e-9)
    return height - marginBottom - ratio * usableHeight
  }

  let dragging = false

  const setT0 = (tVal) => {
    // Update visuals using shared helper
    const snapT = updatePlotMarkerVisuals(plotEl, tVal)
    if (snapT === null) return

    // Sync input
    const input = findInput()
    if (input) {
      input.value = String(snapT)
      const ev = new Event('input', { bubbles: true })
      input.dispatchEvent(ev)
    }
    // If this plot has a modal data window, also trigger overlay redraw to keep overlay marker in sync
    if (plotEl.dataset.windowTMin && typeof applyModalZoomRedraw === 'function') {
      try { applyModalZoomRedraw(plotEl) } catch (_e) { }
    }
  }

  const onMove = (evt) => {
    if (!dragging) return
    const pt = getMousePoint(svg, evt)
    let t
    // If modal data window is active, invert using window domain so handle stays aligned with overlay
    if (plotEl.dataset.windowTMin) {
      const w = parseFloat(svg.getAttribute('data-width') || '480')
      const mL = parseFloat(svg.getAttribute('data-margin-left') || '64')
      const mR = parseFloat(svg.getAttribute('data-margin-right') || '24')
      const usableW = w - mL - mR
      const winMin = parseFloat(plotEl.dataset.windowTMin)
      const winMax = parseFloat(plotEl.dataset.windowTMax)
      const r = (Math.min(Math.max(pt.x, mL), w - mR) - mL) / Math.max(usableW, 1e-9)
      t = winMin + r * (winMax - winMin)
    } else {
      t = invertX(pt.x)
    }
    setT0(t)
  }

  const onUp = () => {
    dragging = false
    // Clear cross-feature drag flag
    try { plotEl.dataset.dragging = '' } catch (_e) { }
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    window.removeEventListener('touchmove', onMove)
    window.removeEventListener('touchend', onUp)
  }

  const startDrag = (evt) => {
    evt.preventDefault()
    evt.stopPropagation()
    dragging = true
    // Hide hint on first interaction
    hideDragHint(plotEl)
    hideExternalHint(plotEl)
    // Mark that t0 is being dragged so zoom/pan ignores this interaction
    try { plotEl.dataset.dragging = 't0' } catch (_e) { }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onUp)
  }

  handle.addEventListener('mousedown', startDrag)
  handle.addEventListener('touchstart', startDrag, { passive: false })

  // Also allow starting drag from the dashed line for convenience (doesn't interfere with spec)
  const dashedLine = t0Group.querySelector('line')
  if (dashedLine) {
    dashedLine.addEventListener('mousedown', startDrag)
    dashedLine.addEventListener('touchstart', startDrag, { passive: false })
  }

  // Show a reliable external hint anchored to the Enlarge button
  showExternalHint(plotEl, slug)
}

function getMousePoint(svg, evt) {
  const isTouch = evt.touches && evt.touches.length
  const clientX = isTouch ? evt.touches[0].clientX : evt.clientX
  const clientY = isTouch ? evt.touches[0].clientY : evt.clientY
  if (typeof SVGSVGElement !== 'undefined' && svg instanceof SVGSVGElement && typeof svg.createSVGPoint === 'function') {
    const pt = svg.createSVGPoint()
    pt.x = clientX
    pt.y = clientY
    const ctm = svg.getScreenCTM()
    if (ctm && typeof ctm.inverse === 'function') {
      const loc = pt.matrixTransform(ctm.inverse())
      return { x: loc.x, y: loc.y }
    }
  }
  // Fallback: approximate using element bounding box scaling
  const rect = svg.getBoundingClientRect()
  const viewBox = svg.viewBox && svg.viewBox.baseVal ? svg.viewBox.baseVal : { x: 0, y: 0, width: parseFloat(svg.getAttribute('data-width') || '480'), height: parseFloat(svg.getAttribute('data-height') || '260') }
  const scaleX = (viewBox.width || 1) / (rect.width || 1)
  const scaleY = (viewBox.height || 1) / (rect.height || 1)
  return {
    x: (clientX - rect.left) * scaleX + (viewBox.x || 0),
    y: (clientY - rect.top) * scaleY + (viewBox.y || 0),
  }
}

// Recompute scales, redraw overlay (axes/grid/path) and reposition t0 for current window
function applyModalZoomRedraw(plotEl) {
  const svg = plotEl.querySelector('[data-chart="preview-svg"]')
  if (!svg) return
  const width = parseFloat(svg.getAttribute('data-width') || svg.getAttribute('width') || '480')
  const height = parseFloat(svg.getAttribute('data-height') || svg.getAttribute('height') || '260')
  const marginLeft = parseFloat(svg.getAttribute('data-margin-left') || '64')
  const marginRight = parseFloat(svg.getAttribute('data-margin-right') || '24')
  const marginTop = parseFloat(svg.getAttribute('data-margin-top') || '24')
  const marginBottom = parseFloat(svg.getAttribute('data-margin-bottom') || '56')
  const minTime = parseFloat(svg.getAttribute('data-min-time') || '0')
  const maxTime = parseFloat(svg.getAttribute('data-max-time') || '1')
  const usableWidth = width - marginLeft - marginRight
  const usableHeight = height - marginTop - marginBottom
  const tMinVis = parseFloat(plotEl.dataset.windowTMin || String(minTime))
  const tMaxVis = parseFloat(plotEl.dataset.windowTMax || String(maxTime))
  const timeToX = (t) => {
    const r = (t - tMinVis) / Math.max(tMaxVis - tMinVis, 1e-9)
    return marginLeft + r * usableWidth
  }

  // Points for this plot (sorted by time)
  let points = []
  try { points = JSON.parse(plotEl.getAttribute('data-points') || '[]') } catch (_e) { points = [] }
  if (!Array.isArray(points) || !points.length) return
  points.sort((a, b) => a[0] - b[0])
  const visPts = points.filter(p => p[0] >= tMinVis && p[0] <= tMaxVis)
  if (!visPts.length) return

  // Autoscale Y in visible window with padding
  let yMin = Infinity, yMax = -Infinity
  for (const [, y] of visPts) { if (y < yMin) yMin = y; if (y > yMax) yMax = y }
  if (!(isFinite(yMin) && isFinite(yMax))) return
  if (yMax === yMin) { yMax += 1; yMin -= 1 }
  const pad = (yMax - yMin) * 0.06
  yMin -= pad; yMax += pad
  const yToPx = (y) => height - marginBottom - ((y - yMin) / Math.max(yMax - yMin, 1e-9)) * usableHeight

  // Ensure overlay group exists
  let overlay = svg.querySelector('[data-zoom-overlay]')
  if (!overlay) {
    overlay = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    overlay.setAttribute('data-zoom-overlay', 'true')
    // Place above base paths but below t0 group for interaction
    const t0 = svg.querySelector('[data-t0-marker]')
    if (t0 && t0.parentNode) t0.parentNode.insertBefore(overlay, t0)
    else svg.appendChild(overlay)
  }
  // Clear overlay
  while (overlay.firstChild) overlay.removeChild(overlay.firstChild)

  // Grid/ticks: nice numbers with target pixel spacing
  const niceStep = (rawStep) => {
    const power = Math.floor(Math.log10(rawStep))
    const base = rawStep / Math.pow(10, power)
    let niceBase
    if (base <= 1) niceBase = 1
    else if (base <= 2) niceBase = 2
    else if (base <= 2.5) niceBase = 2.5
    else if (base <= 5) niceBase = 5
    else niceBase = 10
    return niceBase * Math.pow(10, power)
  }
  const genTicks = (min, max, targetPx, axisPx) => {
    const approxCount = Math.max(2, Math.floor(axisPx / Math.max(1, targetPx)))
    const step = niceStep((max - min) / approxCount)
    const start = Math.ceil(min / step) * step
    const end = Math.floor(max / step) * step
    const ticks = []
    // Always include domain ends
    ticks.push(min)
    for (let v = start; v <= end + 1e-9; v += step) ticks.push(v)
    ticks.push(max)
    // Deduplicate and ensure monotonic increasing
    const uniq = []
    let last = -Infinity
    for (const v of ticks.sort((a, b) => a - b)) {
      if (!uniq.length || Math.abs(v - last) > step * 1e-6) { uniq.push(v); last = v }
    }
    return uniq
  }
  const xticks = genTicks(tMinVis, tMaxVis, 80, usableWidth)
  const yticks = genTicks(yMin, yMax, 56, usableHeight)

  const fmtTime = (v) => {
    const span = Math.abs(tMaxVis - tMinVis)
    const stepGuess = span / Math.max(xticks.length - 1, 1)
    const decimals = stepGuess >= 10 ? 0 : stepGuess >= 1 ? 1 : 2
    return v.toFixed(decimals)
  }
  const fmtTemp = (v) => v.toFixed(1)

  // Draw vertical grid + x labels
  xticks.forEach((t) => {
    const x = timeToX(t)
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.setAttribute('x1', x.toFixed(2))
    line.setAttribute('x2', x.toFixed(2))
    line.setAttribute('y1', (marginTop).toFixed(2))
    line.setAttribute('y2', (height - marginBottom).toFixed(2))
    line.setAttribute('stroke', '#e5e7eb')
    line.setAttribute('stroke-width', '1')
    line.setAttribute('opacity', '0.7')
    overlay.appendChild(line)
    const lbl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    lbl.setAttribute('x', x.toFixed(2))
    lbl.setAttribute('y', (height - marginBottom + 16).toFixed(2))
    lbl.setAttribute('text-anchor', 'middle')
    lbl.setAttribute('font-size', '10')
    lbl.setAttribute('fill', '#6b7280')
    lbl.textContent = fmtTime(t)
    overlay.appendChild(lbl)
  })
  // Draw horizontal grid + y labels
  yticks.forEach((yVal) => {
    const y = yToPx(yVal)
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
    line.setAttribute('x1', (marginLeft).toFixed(2))
    line.setAttribute('x2', (width - marginRight).toFixed(2))
    line.setAttribute('y1', y.toFixed(2))
    line.setAttribute('y2', y.toFixed(2))
    line.setAttribute('stroke', '#e5e7eb')
    line.setAttribute('stroke-width', '1')
    line.setAttribute('opacity', '0.7')
    overlay.appendChild(line)
    const lbl = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    lbl.setAttribute('x', (marginLeft - 6).toFixed(2))
    lbl.setAttribute('y', (y + 3).toFixed(2))
    lbl.setAttribute('text-anchor', 'end')
    lbl.setAttribute('font-size', '10')
    lbl.setAttribute('fill', '#6b7280')
    lbl.textContent = fmtTemp(yVal)
    overlay.appendChild(lbl)
  })

  // Path for visible points
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  let d = ''
  for (let i = 0; i < visPts.length; i++) {
    const [tx, ty] = visPts[i]
    const px = timeToX(tx)
    const py = yToPx(ty)
    d += (i ? 'L' : 'M') + px.toFixed(2) + ' ' + py.toFixed(2)
  }
  path.setAttribute('d', d)
  path.setAttribute('fill', 'none')
  path.setAttribute('stroke', '#2563eb')
  path.setAttribute('stroke-width', '2')
  overlay.appendChild(path)

  // Reposition t0 marker; render marker visuals in overlay to avoid base desync
  const t0Group = svg.querySelector('[data-t0-marker]')
  if (t0Group) {
    const t0 = parseFloat(t0Group.getAttribute('data-t0-seconds') || 'NaN')
    // Expose for ghost locator handler
    plotEl.dataset.t0Seconds = isFinite(t0) ? String(t0) : ''
    if (!Number.isNaN(t0)) {
      const nearest = (() => {
        // binary search on sorted times for stability
        let lo = 0, hi = points.length - 1
        while (lo < hi) {
          const mid = (lo + hi) >> 1
          if (points[mid][0] < t0) lo = mid + 1
          else hi = mid
        }
        const i2 = lo
        const i1 = Math.max(0, i2 - 1)
        const p1 = points[i1]
        const p2 = points[Math.min(points.length - 1, i2)]
        const best = (Math.abs(p1[0] - t0) <= Math.abs(p2[0] - t0)) ? p1 : p2
        return best
      })()
      const [snapT, snapY] = nearest
      // Update t0 attribute to snapped time to keep readout and position in sync
      t0Group.setAttribute('data-t0-seconds', String(snapT))
      const cx = timeToX(snapT)
      const cy = yToPx(snapY)

      // Off-screen handling (use updated t0)
      const offLeft = snapT < tMinVis
      const offRight = snapT > tMaxVis
      const ghostExisting = svg.querySelector('[data-ghost-locator]')
      if (offLeft || offRight) {
        // Hide base marker parts when off-screen
        const baseLine = t0Group.querySelector('line'); if (baseLine) baseLine.setAttribute('visibility', 'hidden')
        const baseCircle = t0Group.querySelector('circle'); if (baseCircle) baseCircle.setAttribute('visibility', 'hidden')
        const baseReadout = t0Group.querySelector('[data-t0-readout]'); if (baseReadout) baseReadout.setAttribute('visibility', 'hidden')
        const handle = svg.querySelector('[data-t0-handle]'); if (handle) handle.setAttribute('visibility', 'hidden')
        // Remove overlay marker if any
        const overlayT0 = svg.querySelector('[data-zoom-t0]'); if (overlayT0) overlayT0.remove()
        if (ghostExisting) ghostExisting.remove()
        const ghost = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        ghost.setAttribute('data-ghost-locator', 'true')
        const gx = offLeft ? marginLeft + 4 : width - marginRight - 4
        const gy = marginTop + usableHeight / 2
        const poly = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        // Small chevron pointing inward
        const dir = offLeft ? 1 : -1
        const size = 10
        const d = `M ${gx + dir * 0} ${gy - size} L ${gx + dir * 8} ${gy} L ${gx + dir * 0} ${gy + size}`
        poly.setAttribute('d', d)
        poly.setAttribute('fill', 'none')
        poly.setAttribute('stroke', '#2563eb')
        poly.setAttribute('stroke-width', '3')
        poly.setAttribute('stroke-linecap', 'round')
        poly.setAttribute('stroke-linejoin', 'round')
        ghost.appendChild(poly)
        ghost.style.cursor = 'pointer'
        ghost.addEventListener('click', () => {
          const ev = new CustomEvent('slp-ghost-click', { bubbles: true })
          plotEl.dispatchEvent(ev)
        })
        svg.appendChild(ghost)
      } else {
        // On-screen: ensure ghost removed
        if (ghostExisting) ghostExisting.remove()
        // Keep base visuals interactive but invisible (opacity 0) so dragging the dashed line works
        const baseLine = t0Group.querySelector('line')
        if (baseLine) {
          baseLine.setAttribute('x1', cx.toFixed(2))
          baseLine.setAttribute('x2', cx.toFixed(2))
          baseLine.style.opacity = '0'
          baseLine.removeAttribute('visibility')
        }
        const baseCircle = t0Group.querySelector('circle')
        if (baseCircle) {
          baseCircle.setAttribute('cx', cx.toFixed(2))
          baseCircle.setAttribute('cy', cy.toFixed(2))
          baseCircle.style.opacity = '0'
          baseCircle.removeAttribute('visibility')
        }
        const baseReadout = t0Group.querySelector('[data-t0-readout]')
        if (baseReadout) {
          baseReadout.style.opacity = '0'
          baseReadout.removeAttribute('visibility')
        }
        // Draw overlay marker (line + circle + readout) with constant sizes
        let overlayT0 = svg.querySelector('[data-zoom-t0]')
        if (overlayT0) overlayT0.remove()
        overlayT0 = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        overlayT0.setAttribute('data-zoom-t0', 'true')
        const vlineO = document.createElementNS('http://www.w3.org/2000/svg', 'line')
        vlineO.setAttribute('x1', cx.toFixed(2))
        vlineO.setAttribute('x2', cx.toFixed(2))
        vlineO.setAttribute('y1', (marginTop).toFixed(2))
        vlineO.setAttribute('y2', (height - marginBottom).toFixed(2))
        vlineO.setAttribute('stroke', '#ea580c')
        vlineO.setAttribute('stroke-width', '2')
        vlineO.setAttribute('stroke-dasharray', '6 6')
        vlineO.style.pointerEvents = 'none'
        overlayT0.appendChild(vlineO)
        const circO = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        circO.setAttribute('cx', cx.toFixed(2))
        circO.setAttribute('cy', cy.toFixed(2))
        circO.setAttribute('r', '3')
        circO.setAttribute('fill', '#f97316')
        circO.style.pointerEvents = 'none'
        overlayT0.appendChild(circO)
        const txtO = document.createElementNS('http://www.w3.org/2000/svg', 'text')
        txtO.setAttribute('x', (cx + 8).toFixed(2))
        txtO.setAttribute('y', (marginTop + 16).toFixed(2))
        txtO.setAttribute('fill', '#ea580c')
        txtO.setAttribute('font-size', '12')
        txtO.setAttribute('data-zoom-t0-readout', 'true')
        txtO.textContent = `t₀: ${snapT.toFixed(3)} s`
        overlayT0.appendChild(txtO)
        // Insert above overlay path but below handle
        const handleRef = svg.querySelector('[data-t0-handle]')
        if (handleRef && handleRef.parentNode) handleRef.parentNode.insertBefore(overlayT0, handleRef)
        else svg.appendChild(overlayT0)
        // Ensure base handle is visible and aligned for dragging
        const handle = svg.querySelector('[data-t0-handle]')
        if (handle) {
          const w = parseFloat(handle.getAttribute('data-width') || handle.getAttribute('width') || '20')
          const tag = handle.tagName.toLowerCase()
          if (tag === 'g') {
            // Always rewrite translate from scratch
            const match = (handle.getAttribute('transform') || 'translate(0,0)').match(/translate\(([-\d\.]+)\s*,\s*([\-\d\.]+)\)/)
            const ty = match ? parseFloat(match[2]) : 0
            const tx = cx - w / 2
            handle.setAttribute('transform', `translate(${tx.toFixed(2)},${ty.toFixed(2)})`)
          } else {
            handle.setAttribute('x', (cx - w / 2).toFixed(2))
          }
          handle.setAttribute('visibility', 'visible')
        }
      }
    }
  }
}

function attachModalZoom(plotEl) {
  const svg = plotEl.querySelector('[data-chart="preview-svg"]')
  if (!svg) return
  let initVB = svg.getAttribute('viewBox')
  if (!initVB) {
    const w = parseFloat(svg.getAttribute('width') || svg.getAttribute('data-width') || '800')
    const h = parseFloat(svg.getAttribute('height') || svg.getAttribute('data-height') || '400')
    initVB = `0 0 ${w} ${h}`
    svg.setAttribute('viewBox', initVB)
  }
  plotEl.dataset.viewboxInit = initVB
  if (!plotEl.dataset.viewbox) plotEl.dataset.viewbox = initVB

  // Ensure base layers are hidden to avoid mixed duplicate plots under overlay
  ensureModalOverlayStyling(svg)

  // Initialize data-window state (tMin..tMax); keep viewBox fixed
  const width = parseFloat(svg.getAttribute('data-width') || svg.getAttribute('width') || '480')
  const height = parseFloat(svg.getAttribute('data-height') || svg.getAttribute('height') || '260')
  const marginLeft = parseFloat(svg.getAttribute('data-margin-left') || '64')
  const marginRight = parseFloat(svg.getAttribute('data-margin-right') || '24')
  const marginTop = parseFloat(svg.getAttribute('data-margin-top') || '24')
  const marginBottom = parseFloat(svg.getAttribute('data-margin-bottom') || '56')
  const usableWidth = width - marginLeft - marginRight
  const datasetMinT = parseFloat(svg.getAttribute('data-min-time') || '0')
  const datasetMaxT = parseFloat(svg.getAttribute('data-max-time') || '1')

  if (!plotEl.dataset.windowTMin) plotEl.dataset.windowTMin = String(datasetMinT)
  if (!plotEl.dataset.windowTMax) plotEl.dataset.windowTMax = String(datasetMaxT)

  const getWindow = () => {
    let tMin = parseFloat(plotEl.dataset.windowTMin || String(datasetMinT))
    let tMax = parseFloat(plotEl.dataset.windowTMax || String(datasetMaxT))
    if (!(isFinite(tMin) && isFinite(tMax)) || tMax <= tMin) {
      tMin = datasetMinT; tMax = datasetMaxT
    }
    return { tMin, tMax }
  }
  const setWindow = (tMin, tMax) => {
    // Clamp to dataset and shift if out of bounds
    const span = Math.max(1e-9, tMax - tMin)
    if (tMin < datasetMinT) { tMax = Math.min(datasetMaxT, datasetMinT + span); tMin = tMax - span }
    if (tMax > datasetMaxT) { tMin = Math.max(datasetMinT, datasetMaxT - span); tMax = tMin + span }
    plotEl.dataset.windowTMin = String(tMin)
    plotEl.dataset.windowTMax = String(tMax)
  }
  const xScale = (t) => {
    const { tMin, tMax } = getWindow()
    const r = (t - tMin) / Math.max(tMax - tMin, 1e-9)
    return marginLeft + r * usableWidth
  }
  const xInvert = (px) => {
    const { tMin, tMax } = getWindow()
    const r = (px - marginLeft) / Math.max(usableWidth, 1e-9)
    return tMin + r * (tMax - tMin)
  }

  const parseVB = (s) => {
    const a = (s || '0 0 100 100').split(/\s+/).map(parseFloat)
    return { x: a[0] || 0, y: a[1] || 0, w: a[2] || 100, h: a[3] || 100 }
  }
  const setVB = (vb) => {
    const init = parseVB(plotEl.dataset.viewboxInit)
    const w = Math.max(Math.min(vb.w, init.w), Math.max(10, init.w * 0.02))
    const h = Math.max(Math.min(vb.h, init.h), Math.max(10, init.h * 0.02))
    const maxX = init.x + init.w - w
    const maxY = init.y + init.h - h
    const x = Math.min(Math.max(vb.x, init.x), maxX)
    const y = Math.min(Math.max(vb.y, init.y), maxY)
    const s = `${x} ${y} ${w} ${h}`
    svg.setAttribute('viewBox', s)
    plotEl.dataset.viewbox = s
    plotEl.dataset.zoom = (w < init.w || h < init.h) ? 'active' : 'idle'
    // Data-aware redraw overlay (axes/grid/path/marker) for current window
    try { applyModalZoomRedraw(plotEl) } catch (_e) { /* noop */ }
  }
  const getSvgPoint = (evt) => {
    const isTouch = evt.touches && evt.touches.length
    const clientX = isTouch ? evt.touches[0].clientX : evt.clientX
    const clientY = isTouch ? evt.touches[0].clientY : evt.clientY
    const pt = svg.createSVGPoint()
    pt.x = clientX; pt.y = clientY
    const ctm = svg.getScreenCTM()
    if (ctm && typeof ctm.inverse === 'function') {
      const loc = pt.matrixTransform(ctm.inverse())
      return { x: loc.x, y: loc.y }
    }
    const rect = svg.getBoundingClientRect()
    const vb = svg.viewBox && svg.viewBox.baseVal ? svg.viewBox.baseVal : { x: 0, y: 0, width: parseFloat(svg.getAttribute('data-width') || '480'), height: parseFloat(svg.getAttribute('data-height') || '260') }
    const scaleX = (vb.width || 1) / (rect.width || 1)
    const scaleY = (vb.height || 1) / (rect.height || 1)
    return { x: (clientX - rect.left) * scaleX + (vb.x || 0), y: (clientY - rect.top) * scaleY + (vb.y || 0) }
  }

  const onClick = (evt) => {
    // Skip if a drag just happened or t0 is being dragged
    if (plotEl.dataset.dragging === 't0' || plotEl.dataset.panActive === '1' || plotEl.dataset.suppressNextClick === '1') {
      try { delete plotEl.dataset.suppressNextClick } catch (_e) { }
      return
    }
    const t = evt.target
    if (t && t.closest && (t.closest('[data-t0-handle]') || t.closest('[data-t0-marker]'))) return
    // Ignore multi-touch
    if (evt.touches && evt.touches.length !== 1) return
    const pt = getSvgPoint(evt)
    const clickT = xInvert(pt.x)
    const { tMin, tMax } = getWindow()
    const half = (tMax - tMin) / 2
    const zoomFactor = evt.shiftKey ? 1.5 : (1 / 1.5) // shift: zoom out, otherwise zoom in
    const newHalf = Math.max(1e-9, half * zoomFactor)
    let nMin = clickT - newHalf
    let nMax = clickT + newHalf
    setWindow(nMin, nMax)
    applyModalZoomRedraw(plotEl)
  }
  svg.addEventListener('click', onClick)

  let panning = false
  let startClient = null
  let startWindow = null
  let secondsPerPixel = 0
  let panFrame = null
  let panNext = null
  const DRAG_THRESHOLD = 5

  const onPanStart = (evt) => {
    const t = evt.target
    if (t && t.closest && (t.closest('[data-t0-handle]') || t.closest('[data-t0-marker]'))) return
    if (evt.button !== undefined && evt.button !== 0) return
    // Ignore multitouch for stability
    if (evt.touches && evt.touches.length !== 1) return
    panning = true
    // Cache start states for data-window pan
    const rect = svg.getBoundingClientRect()
    startClient = { x: (evt.touches ? evt.touches[0].clientX : evt.clientX) || 0, y: (evt.touches ? evt.touches[0].clientY : evt.clientY) || 0 }
    startWindow = getWindow()
    secondsPerPixel = (startWindow.tMax - startWindow.tMin) / Math.max(1, rect.width - (width - usableWidth))
    try { plotEl.dataset.panActive = '0' } catch (_e) { }
    // Improve UX while panning
    const prevUserSelect = document.body.style.userSelect
    document.body.dataset._slpUserSelect = prevUserSelect || ''
    document.body.style.userSelect = 'none'
    svg.style.cursor = 'grabbing'
    window.addEventListener('mousemove', onPanMove)
    window.addEventListener('mouseup', onPanEnd)
    window.addEventListener('touchmove', onPanMove, { passive: false })
    window.addEventListener('touchend', onPanEnd)
  }
  const onPanMove = (evt) => {
    if (!panning || !startWindow || !startClient) return
    evt.preventDefault()
    const cx = (evt.touches ? evt.touches[0].clientX : evt.clientX) || 0
    const cy = (evt.touches ? evt.touches[0].clientY : evt.clientY) || 0
    const moved = Math.hypot(cx - startClient.x, cy - startClient.y)
    if (moved > DRAG_THRESHOLD && plotEl.dataset.panActive !== '1') {
      try { plotEl.dataset.panActive = '1'; plotEl.dataset.suppressNextClick = '1' } catch (_e) { }
    }
    // Convert client delta to time delta using window scale
    const dxPx = (cx - startClient.x)
    const dt = dxPx * secondsPerPixel
    const nMin = startWindow.tMin - dt
    const nMax = startWindow.tMax - dt
    panNext = { tMin: nMin, tMax: nMax }
    if (!panFrame) {
      panFrame = requestAnimationFrame(() => {
        if (panNext) { setWindow(panNext.tMin, panNext.tMax); applyModalZoomRedraw(plotEl) }
        panFrame = null
      })
    }
  }
  const onPanEnd = () => {
    panning = false
    startClient = null
    startWindow = null
    panNext = null
    if (panFrame) cancelAnimationFrame(panFrame)
    panFrame = null
    window.removeEventListener('mousemove', onPanMove)
    window.removeEventListener('mouseup', onPanEnd)
    window.removeEventListener('touchmove', onPanMove)
    window.removeEventListener('touchend', onPanEnd)
    // Clear pan active after a tick so click handler can see suppress flag
    setTimeout(() => { try { delete plotEl.dataset.panActive } catch (_e) { } }, 0)
    // Restore UX state
    svg.style.cursor = ''
    if (document.body.dataset._slpUserSelect !== undefined) {
      document.body.style.userSelect = document.body.dataset._slpUserSelect
      delete document.body.dataset._slpUserSelect
    }
  }
  svg.addEventListener('mousedown', onPanStart)
  svg.addEventListener('touchstart', onPanStart, { passive: false })

  const resetBtn = plotEl.parentElement?.querySelector('[data-action="reset-zoom"]')
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      setWindow(datasetMinT, datasetMaxT)
      applyModalZoomRedraw(plotEl)
    })
  }

  // Ghost locator: recenter on t0 without changing zoom
  plotEl.addEventListener('slp-ghost-click', () => {
    const t0 = parseFloat(plotEl.dataset.t0Seconds || 'NaN')
    if (!Number.isNaN(t0)) {
      const { tMin, tMax } = getWindow()
      const half = (tMax - tMin) / 2
      setWindow(t0 - half, t0 + half)
      applyModalZoomRedraw(plotEl)
    }
  })

  // Initial redraw for current view
  try { applyModalZoomRedraw(plotEl) } catch (_e) { }
}

// Attach modal t₀ input handler: autofocus on change and sync to main card
function attachModalT0Input(plotEl, slug) {
  const modalInput = plotEl.parentElement?.querySelector('[data-modal-t0-input]')
  if (!modalInput) return

  const mainInput = document.querySelector(`input[name="t0_seconds[${CSS.escape(slug)}]"]`)

  modalInput.addEventListener('input', (evt) => {
    const value = parseFloat(evt.target.value)
    if (!Number.isNaN(value)) {
      // Update main card input
      if (mainInput) {
        mainInput.value = String(value)
        const inputEvent = new Event('input', { bubbles: true })
        mainInput.dispatchEvent(inputEvent)
      }
      // Update t₀ marker position
      const svg = plotEl.querySelector('[data-chart="preview-svg"]')
      const t0Group = svg?.querySelector('[data-t0-marker]')
      if (t0Group) {
        t0Group.setAttribute('data-t0-seconds', String(value))
      }
      // Autofocus: pan view to center on new t₀
      if (plotEl.dataset.windowTMin && plotEl.dataset.windowTMax) {
        const tMin = parseFloat(plotEl.dataset.windowTMin)
        const tMax = parseFloat(plotEl.dataset.windowTMax)
        const half = (tMax - tMin) / 2
        plotEl.dataset.windowTMin = String(value - half)
        plotEl.dataset.windowTMax = String(value + half)
        // Trigger redraw to update view and marker position
        try { applyModalZoomRedraw(plotEl) } catch (_e) { }
      }
    }
  })
}
